import { z } from 'zod';
import { IdZ, TeamZ, TsZ, textZ } from './common';

/**
 * The game event envelope and union (R2, frozen). The device-made uuidv7 `id` is the only
 * dedupe key. Points are never stored. `epoch` is the keeper epoch it was written under.
 *
 * Clocks: `clientTs` is the device's corrected wall clock (`serverNow()` on the client).
 * `tMs` is `clientTs - game.playedAt`, clamped at 0, on EVERY device. All phones share that one
 * origin, so after a keeper handoff the new phone's events keep sorting after the old phone's by
 * (tMs, deviceId, seq, id) and a void of the previous keeper's bucket still points backwards.
 * Never restart tMs at 0 on a second device. Fixture `handoffVoid2v2` pins this.
 *
 * targetId: required on `void` (the event undone). On any other type it means "this
 * event supersedes the target" (the reducer supports it; the v1 UI only writes void plus
 * re-entry, RULINGS cut list).
 */

export const SHOT_CLASSES = ['rim', 'mid', 'two', 'three'] as const;
export const ShotClassZ = z.enum(SHOT_CLASSES);
export type ShotClass = z.infer<typeof ShotClassZ>;

export const GAME_EVENT_TYPES = [
  'shot',
  'ft',
  'rebound',
  'steal',
  'turnover',
  'foul',
  'sub',
  'score_adjust',
  'end',
  'void',
] as const;
export type GameEventType = (typeof GAME_EVENT_TYPES)[number];

export const END_REASONS = ['target', 'cap', 'time', 'forfeit', 'manual', 'abandoned'] as const;

const base = {
  id: IdZ,
  gameId: IdZ,
  deviceId: IdZ,
  /** Per device per game, from 1. */
  seq: z.number().int().positive(),
  epoch: z.number().int().nonnegative(),
  tMs: z.number().int().nonnegative(),
  clientTs: TsZ,
  authorMemberId: IdZ,
  playerId: IdZ.nullable().optional(),
  team: TeamZ.nullable().optional(),
  targetId: IdZ.nullable().optional(),
  schemaVersion: z.literal(1),
};

export const ShotEventZ = z.object({
  ...base,
  type: z.literal('shot'),
  playerId: IdZ,
  data: z.object({
    made: z.boolean(),
    shotClass: ShotClassZ,
    assistBy: IdZ.optional(),
    blockedBy: IdZ.optional(),
  }),
});

export const FtEventZ = z.object({
  ...base,
  type: z.literal('ft'),
  playerId: IdZ,
  data: z.object({ made: z.boolean() }),
});

/** playerId null with team set is a team rebound (nobody credited). */
export const ReboundEventZ = z.object({
  ...base,
  type: z.literal('rebound'),
  data: z.object({ kind: z.enum(['off', 'def']) }),
});

/** A steal with `from` also counts a turnover for `from`. Do not also write a turnover. */
export const StealEventZ = z.object({
  ...base,
  type: z.literal('steal'),
  playerId: IdZ,
  data: z.object({ from: IdZ.optional() }),
});

export const TurnoverEventZ = z.object({
  ...base,
  type: z.literal('turnover'),
  playerId: IdZ,
  data: z.object({ kind: z.enum(['travel', 'out', 'offensive_foul', 'other']).optional() }),
});

export const FoulEventZ = z.object({
  ...base,
  type: z.literal('foul'),
  playerId: IdZ,
  data: z.object({ on: IdZ.optional(), shooting: z.boolean().optional() }),
});

/** playerId comes in, data.out goes out. Both on the same team. */
export const SubEventZ = z.object({
  ...base,
  type: z.literal('sub'),
  playerId: IdZ,
  data: z.object({ out: IdZ }),
});

/** Team points nobody is credited with (R9 "value with no player", quick and final games). */
export const ScoreAdjustEventZ = z.object({
  ...base,
  type: z.literal('score_adjust'),
  team: TeamZ,
  data: z.object({
    points: z
      .number()
      .int()
      .min(-200)
      .max(200)
      .refine((n) => n !== 0, 'points must not be 0'),
    reason: textZ(1, 80).optional(),
  }),
});

/** Locks the game (R5). reason 'abandoned' gives status abandoned, the rest final. */
export const EndEventZ = z.object({
  ...base,
  type: z.literal('end'),
  data: z.object({ reason: z.enum(END_REASONS) }),
});

export const VoidEventZ = z.object({
  ...base,
  type: z.literal('void'),
  targetId: IdZ,
  data: z.object({}).optional().default({}),
});

export const GameEventZ = z.discriminatedUnion('type', [
  ShotEventZ,
  FtEventZ,
  ReboundEventZ,
  StealEventZ,
  TurnoverEventZ,
  FoulEventZ,
  SubEventZ,
  ScoreAdjustEventZ,
  EndEventZ,
  VoidEventZ,
]);
export type GameEvent = z.infer<typeof GameEventZ>;
export type GameEventInput = z.input<typeof GameEventZ>;
export type GameEventOf<T extends GameEventType> = Extract<GameEvent, { type: T }>;

/** A game event as stored and served back (pull and SSE). */
export const StoredGameEventZ = z.intersection(
  GameEventZ,
  z.object({ serverSeq: z.number().int().positive(), receivedAt: TsZ }),
);
export type StoredGameEvent = z.infer<typeof StoredGameEventZ>;

/**
 * Game Lab events reuse the envelope with runId in place of gameId (add-gamelab.md section 4).
 * The `data` of start/action is checked further by the Lab interpreter.
 */
export const LAB_EVENT_TYPES = ['start', 'action', 'timer_expired', 'void', 'end', 'abandon'] as const;
const labBase = {
  id: IdZ,
  runId: IdZ,
  deviceId: IdZ,
  seq: z.number().int().positive(),
  epoch: z.number().int().nonnegative(),
  tMs: z.number().int().nonnegative(),
  clientTs: TsZ,
  authorMemberId: IdZ,
  targetId: IdZ.nullable().optional(),
  schemaVersion: z.literal(1),
};
const LabScalar = z.union([z.number().int(), z.boolean(), z.string().max(80)]);
export const LabEventZ = z.discriminatedUnion('type', [
  z.object({
    ...labBase,
    type: z.literal('start'),
    data: z.object({
      defId: IdZ,
      seed: z.number().int().min(0).max(0xffffffff),
      order: z.array(IdZ).min(1).max(16),
      teamOf: z.record(z.string(), z.string().max(8)).optional(),
      options: z.record(z.string(), LabScalar),
      sessionId: IdZ.nullable().optional(),
    }),
  }),
  z.object({
    ...labBase,
    type: z.literal('action'),
    data: z.object({
      action: z.string().min(1).max(40),
      actor: IdZ.optional(),
      arg: z.union([z.string().max(80), z.number().int()]).optional(),
    }),
  }),
  z.object({
    ...labBase,
    type: z.literal('timer_expired'),
    data: z.object({ timer: z.string().min(1).max(40), startedT: z.number().int().nonnegative() }),
  }),
  z.object({ ...labBase, type: z.literal('void'), targetId: IdZ, data: z.object({}).optional().default({}) }),
  z.object({ ...labBase, type: z.literal('end'), data: z.object({}).optional().default({}) }),
  z.object({ ...labBase, type: z.literal('abandon'), data: z.object({}).optional().default({}) }),
]);
export type LabEvent = z.infer<typeof LabEventZ>;

export const StoredLabEventZ = z.intersection(
  LabEventZ,
  z.object({ serverSeq: z.number().int().positive(), receivedAt: TsZ }),
);
export type StoredLabEvent = z.infer<typeof StoredLabEventZ>;
